import React from 'react';
import LandingLayout from '../layouts/LandingLayout';
import { motion } from 'framer-motion';
import { Calendar, Zap, Shield, Users, Clock, Sparkles, Target, Heart } from 'lucide-react';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const values = [
  {
    icon: <Clock className="h-6 w-6" />,
    title: "Speed & Simplicity",
    description: "We believe that the best technology feels invisible. Booking an appointment should take seconds, not minutes."
  },
  {
    icon: <Shield className="h-6 w-6" />,
    title: "Professional Presentation",
    description: "Your online presence matters. CitaLoo gives you the tools to present yourself professionally to clients."
  },
  {
    icon: <Target className="h-6 w-6" />,
    title: "Respect for Your Time",
    description: "We know your time is valuable—and billable. Every feature helps you manage, protect, and monetize your time."
  },
  {
    icon: <Sparkles className="h-6 w-6" />,
    title: "Continuous Improvement",
    description: "We're never satisfied with 'good enough.' Our team constantly refines CitaLoo based on user feedback."
  },
  {
    icon: <Heart className="h-6 w-6" />,
    title: "Affordability",
    description: "Powerful tools shouldn't break the bank. We keep our pricing fair and transparent for everyone."
  }
];

const About: React.FC = () => {
  return (
    <LandingLayout>
      <div className="min-h-screen">
        {/* Hero Section */}
        <motion.section 
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="pt-32 pb-16 px-4 md:px-6 max-w-7xl mx-auto"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/70">
            About CitaLoo
          </h1>
          <p className="text-xl text-center text-muted-foreground max-w-3xl mx-auto">
            Scheduling shouldn't be complicated, but somehow it always was. We're changing that.
          </p>
        </motion.section>

        {/* Our Story Section */}
        <motion.section 
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="py-16 px-4 md:px-6 bg-muted/30"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div variants={fadeIn} className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <p className="text-muted-foreground mb-6">
                Like many of you, I found myself juggling multiple tools just to book a simple meeting. 
                I needed one platform for calendar management, another for client intake forms, and yet 
                another for payment processing. The constant switching between services was not just 
                inefficient—it was maddening.
              </p>
              <p className="text-muted-foreground mb-6">
                That's when the idea for CitaLoo was born—a platform designed to make booking 
                appointments as fast and easy as possible.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Values Section */}
        <motion.section 
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="py-16 px-4 md:px-6"
        >
          <div className="max-w-7xl mx-auto">
            <motion.h2 variants={fadeIn} className="text-3xl font-bold mb-12 text-center">
              Our Values
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn}
                  className="p-6 rounded-2xl bg-card border border-border/50 hover:border-primary/50 transition-colors duration-300"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="p-3 rounded-xl bg-primary/10 text-primary">
                      {value.icon}
                    </div>
                    <h3 className="text-xl font-semibold">{value.title}</h3>
                  </div>
                  <p className="text-muted-foreground">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* AI Section */}
        <motion.section 
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="py-16 px-4 md:px-6 bg-muted/30"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div variants={fadeIn} className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-6">
                <Zap className="h-8 w-8" />
              </div>
              <h2 className="text-3xl font-bold mb-6">The AI Difference</h2>
              <p className="text-muted-foreground mb-6">
                While simplicity was our starting point, we knew we could go further. 
                CitaLoo's intelligent features work quietly in the background, learning 
                from your scheduling patterns and preferences to make booking even more effortless.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Join Section */}
        <motion.section 
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          className="py-16 px-4 md:px-6 text-center"
        >
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Join Our Journey</h2>
            <p className="text-muted-foreground mb-8">
              CitaLoo is more than just a scheduling tool—it's a movement toward simpler, 
              more intelligent ways of working and getting paid for your expertise.
            </p>
            <div className="inline-flex items-center space-x-2 text-sm text-muted-foreground bg-muted px-4 py-2 rounded-full">
              <Users className="h-4 w-4" />
              <span>Join thousands of professionals who trust CitaLoo</span>
            </div>
          </div>
        </motion.section>
      </div>
    </LandingLayout>
  );
};

export default About; 